/**
 * Header loan-switcher visibility gate.
 *
 * NP-178 (AC18, Jul 13 update) originally hid the selector until the borrower
 * had a submitted-or-beyond loan. NP-231 (supersedes NP-70/NP-178 on this
 * point) widens the gate: in-progress and active loans surface the switcher
 * too, so a borrower working a draft can still see and switch between loans.
 *
 * Status source: `GET /api/v1/loans` LoanSummary.Status is a display cache of
 * the latest Vesta `LoanStatuses` entry (PascalCase enum names, e.g.
 * "Submitted", "ConditionallyApproved", "ClearToClose"), seeded as
 * "In Progress" at create. Vesta reports loans as "Active" while the borrower
 * is still filling the wizard. Legacy/mock rows may carry spaced variants
 * ("Application Submitted") — normalisation strips spaces/underscores/hyphens.
 *
 * Still a positive allowlist — deliberately excluded:
 *  - "PreQualified" / "PreApproved" — prospect stages before an application
 *    exists in earnest.
 *  - Terminal negatives ("Denied", "Withdrawn", "Cancelled", "Suspended").
 */

const SWITCHER_VISIBLE = new Set([
  // NP-231: In Progress/Active (wizard draft applications) now surface the
  // switcher. Note: the literal 'Draft' status is intentionally NOT included.
  'INPROGRESS',
  'ACTIVE',
  // Submitted-or-beyond (NP-178 AC18 set).
  'SUBMITTED',
  'APPLICATIONSUBMITTED',
  'INREVIEW',
  'PROCESSING',
  'UNDERWRITING',
  'APPROVED',
  'CONDITIONALLYAPPROVED',
  'CLEARTOCLOSE',
  'CLOSED',
  'FUNDED',
  'POSTCLOSE',
  'POSTCLOSING',
  'COMPLETED',
]);

/** True when a loan status string should surface the header loan switcher. */
export function isSwitcherVisibleStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return SWITCHER_VISIBLE.has(status.toUpperCase().replace(/[\s_-]/g, ''));
}

/**
 * NP-234 — loan status → index into the Dashboard "Loan journey" steps
 * (Application Submitted, Under Review, Document Collection, Approved,
 * Clear to Close, Funded). Moved off the vertical My Loan timeline when the
 * journey became a horizontal strip on the Dashboard.
 *
 * "Active" is deliberately NOT a progressed step. Vesta reports loans as
 * "Active" from the wizard onward (see loanResultToLoanData in LoanContext),
 * so a freshly submitted loan reaches the submitted Dashboard still carrying
 * status "Active". The old My Loan helper mapped ACTIVE to Document
 * Collection, which on that screen showed a borrower three steps further along
 * than they are. Unrecognised — including bare Active/Submitted — is step 0.
 *
 * Normalised the same way as the switcher gate above, so spaced and
 * underscored variants ("Clear to close", "CLEAR_TO_CLOSE") both match.
 * Order matters: FUNDED/CLOSED is tested before the CLOSE-family steps.
 *
 * "CTC" is matched whole, not as a substring — it is an abbreviation that
 * arrives as the entire status, and a substring test would send any future
 * status that happened to contain those three letters to Clear to Close.
 *
 * "CLOSED" is guarded against "DISCLOSED", which contains it: without the
 * guard, a Disclosed / ActiveDisclosed loan reads as Funded — the last step
 * instead of the first. Latent while the strip only renders on the not-yet-
 * disclosed dashboard, but this helper is exported and the obvious next step
 * for NP-234 is rendering the strip on disclosed states too.
 *
 * Terminal statuses (Denied, Withdrawn, Cancelled) land on step 0. That is a
 * gap, but not this helper's to close: deriveLifecycleState is flag-based and
 * never considers them, so a denied loan renders the whole submitted dashboard
 * — green success banner, Pending items and all — of which the journey strip is
 * only one part. Worth a ticket; a fix here alone would just be inconsistent.
 */
/**
 * NP-527 — Vesta's MILESTONE is the pipeline stage (`currentStage`), not `status`.
 * Moving a loan's milestone in Vesta changes the stage and leaves the status alone,
 * so a journey driven off status never moved: the borrower's strip sat where it was
 * no matter what an internal user did. This maps the stage instead.
 *
 * Exact matches against the AFN workspace's stage templates (the list NP-72
 * documents in lifecycle.ts), because the vocabulary is closed and known. An
 * unrecognised or missing stage returns null so the caller can fall back to the
 * status mapping rather than silently claiming step 0.
 *
 * Two things a reviewer should know:
 *  • "Document Collection" is unreachable from a stage — Vesta has no equivalent.
 *    It can still be reached through the status fallback. The six step labels came
 *    from the old My Loan list and predate this mapping; if the labels should mirror
 *    Vesta's pipeline instead, that is a BA call and a bigger change than this fix.
 *  • Terminal stages (Canceled, Denied, Denied review) are deliberately unmapped and
 *    fall through, because a denied loan renders the whole submitted dashboard —
 *    green success banner and all — and fixing the strip alone would be inconsistent.
 */
const STAGE_STEP: ReadonlyMap<string, number> = new Map([
  ['PROSPECTS', 0],
  ['FILESTART', 0],
  ['SETUPPROCESSING', 1],
  ['SUBMITTEDTOUW', 1],
  ['RESUBMITTED', 1],
  ['SUSPENDED', 1],
  ['CONDITIONALAPPROVAL', 3],
  ['CLEARTOCLOSE', 4],
  ['CLOSING', 4],
  ['FUNDED', 5],
  ['SHIPPING', 5],
  ['POSTCLOSING', 5],
  ['PURCHASED', 5],
]);

export function loanJourneyStepFromStage(stage: string | null | undefined): number | null {
  const s = (stage ?? '').toUpperCase().replace(/[\s_/-]/g, '');
  return STAGE_STEP.get(s) ?? null;
}

export function loanJourneyStep(status: string | null | undefined): number {
  const s = (status ?? '').toUpperCase().replace(/[\s_-]/g, '');
  if (s.includes('FUNDED') || (s.includes('CLOSED') && !s.includes('DISCLOSED')) || s.includes('POSTCLOS')) return 5;
  if (s.includes('CLEARTOCLOSE') || s === 'CTC' || s.includes('CLOSING')) return 4;
  if (s.includes('APPROVED')) return 3;
  if (s.includes('DOCUMENT')) return 2;
  if (s.includes('REVIEW') || s.includes('PROCESSING') || s.includes('UNDERWRIT')) return 1;
  return 0;
}

/** Gate: at least one of the borrower's loans is in-progress-or-beyond. */
export function hasSwitcherVisibleLoan(loans: ReadonlyArray<{ status?: string | null }>): boolean {
  return loans.some((l) => isSwitcherVisibleStatus(l.status));
}

/**
 * NP-431 — the single place that decides which loans COUNT for "does this
 * account have exactly one loan?".
 *
 * NP-70's AC (BA Paolo Domingo): "If borrower has 1 loan: static display with
 * '+Start a New Loan' CTA" and the switcher is "Not visible in States 1 (No
 * App) or 2 (Partial App) — only appears in States 3 and 4". So the count must
 * consider only loans eligible for States 3/4 — exactly the switcher allowlist
 * above — not every row `GET /api/v1/loans` returns.
 *
 * Deliberately also excludes ids known to be unreachable: NP-424 found legacy
 * incomplete loans with no Vesta link that permanently 404, and an account
 * whose only "extra" loan is one of those should still count as a 1-loan
 * account.
 *
 * ⚠️ OPEN (Rock → BA, 2026-07-28): confirm whether partial/dead/archived loans
 * should count toward the total. This helper is the ONE place to change if the
 * answer differs — callers only ask "is there exactly one?".
 */
export function eligibleForAutoSelect<T extends { loanId?: string | null; status?: string | null }>(
  loans: ReadonlyArray<T>,
  options?: { excludeLoanIds?: ReadonlySet<string> },
): T[] {
  const excluded = options?.excludeLoanIds;
  return loans.filter(
    (l) => !!l.loanId && isSwitcherVisibleStatus(l.status) && !excluded?.has(l.loanId),
  );
}

/**
 * NP-600 — the loan to adopt when nothing is selected yet: the FIRST eligible one.
 *
 * This supersedes NP-431's sole-loan-only rule for the adoption decision, and with it
 * NP-70's "with 2+ loans the app never guesses". That rule was never delivered as
 * written: `LoanSwitcher` renders `loans.find(active) ?? loans[0]`, so a borrower with
 * no selection is ALREADY shown the first loan in the header. What they got was not an
 * explicit pick — it was a loan named in the header while the app behaved as though they
 * had none: `useLoanQuery(undefined)` never fetched, the lifecycle fell to
 * PROSPECT_NO_APP, and My Loan and Contacts disappeared (NP-600, reported on mobile
 * because a fresh device is where no persisted selection exists).
 *
 * Adopting the first eligible loan makes the state agree with what the switcher already
 * shows. The order is the API's — `GET /api/v1/loans` sorts by UpdatedAt descending — so
 * "first" is the most recently touched loan, and it is the same element the switcher
 * displays because both filter by `isSwitcherVisibleStatus` and neither re-sorts.
 *
 * Still nothing to adopt when the account has no eligible loan, and callers remain
 * responsible for honouring "Start a New Loan" (`newLoanIntent`) before calling.
 */
export function firstAutoSelectableLoanId(
  loans: ReadonlyArray<{ loanId?: string | null; status?: string | null }>,
  options?: { excludeLoanIds?: ReadonlySet<string> },
): string | undefined {
  return eligibleForAutoSelect(loans, options)[0]?.loanId ?? undefined;
}

/**
 * NP-431 — the id of the account's ONLY auto-selectable loan, or undefined when
 * there are zero or 2+ (ambiguous: per NP-70's 2+ AC the user picks from the
 * dropdown). Auto-selecting the sole loan is not a guess — it is the only
 * possible outcome of the manual step it replaces.
 */
export function soleAutoSelectableLoanId(
  loans: ReadonlyArray<{ loanId?: string | null; status?: string | null }>,
  options?: { excludeLoanIds?: ReadonlySet<string> },
): string | undefined {
  const eligible = eligibleForAutoSelect(loans, options);
  return eligible.length === 1 ? eligible[0].loanId ?? undefined : undefined;
}

/** NP-663: the six stages an Encompass loan's journey is shown in, in order. */
export const ENCOMPASS_JOURNEY_STAGES = ['Lead', 'Application', 'Documents', 'Approval', 'CTC', 'Funded'] as const;
export type EncompassJourneyStage = (typeof ENCOMPASS_JOURNEY_STAGES)[number];

/**
 * Keyed by the API's currentStage values for an Encompass loan plus NP-663's milestone table
 * (case-insensitive, exact).
 */
const ENCOMPASS_MILESTONE_STAGE = new Map<string, EncompassJourneyStage>([
  ['prospect', 'Lead'],
  ['started', 'Application'],
  ['application', 'Application'],
  ['submitted', 'Documents'],
  ['processing', 'Documents'],
  ['underwriting', 'Approval'],
  ['approved', 'Approval'],
  ['clear to close', 'CTC'],
  ['closed', 'Funded'],
  ['funded', 'Funded'],
  // NP-755: what the API actually sends in currentStage for an Encompass loan
  // (afn-path-api EncompassMilestoneStage, #355): Vesta stage names, not milestones.
  // Without these, every loan short of CTC falls through to "In Progress".
  ['prospects', 'Lead'],
  ['file start', 'Application'],
  ['setup/processing', 'Documents'],
  ['conditional approval', 'Approval'],
  // 'clear to close' → CTC and 'funded' → Funded are already above.
]);

export interface EncompassJourney {
  /** The stage to name to the borrower — "In Progress" for a milestone the table does not map. */
  label: EncompassJourneyStage | 'In Progress';
  /** Stages before this index are complete; this one is current. Equal to the stage count once funded. */
  activeIdx: number;
  isFunded: boolean;
}

/**
 * NP-663: a mapped milestone completes its stage and every stage before it (Lead stays
 * current only for Prospect). An unmapped milestone is Application, in progress — no
 * nearest-match guessing.
 */
export function encompassJourney(milestone: string | null | undefined): EncompassJourney {
  const stage = ENCOMPASS_MILESTONE_STAGE.get((milestone ?? '').trim().toLowerCase());
  if (!stage) return { label: 'In Progress', activeIdx: 1, isFunded: false };
  if (stage === 'Lead') return { label: stage, activeIdx: 0, isFunded: false };
  return {
    label: stage,
    activeIdx: ENCOMPASS_JOURNEY_STAGES.indexOf(stage) + 1,
    isFunded: stage === 'Funded',
  };
}
